import React, { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { auth } from "../../firebase";
import styles from "./QRAuthCode.module.scss";

const WINDOW_SECONDS = 30;

function currentWindow(): number {
  return Math.floor(Date.now() / 1000 / WINDOW_SECONDS);
}

function secondsUntilNextWindow(): number {
  return WINDOW_SECONDS - (Math.floor(Date.now() / 1000) % WINDOW_SECONDS);
}

function buildQRValue(uid: string): string {
  return `CO:${uid}:${currentWindow()}`;
}

interface QRAuthCodeProps {
  onClose: () => void;
}

const QRAuthCode: React.FC<QRAuthCodeProps> = ({ onClose }) => {
  const uid = auth.currentUser?.uid ?? null;
  const [qrValue, setQrValue] = useState(() => (uid ? buildQRValue(uid) : ""));
  const [secondsLeft, setSecondsLeft] = useState(secondsUntilNextWindow());

  useEffect(() => {
    if (!uid) return;

    const tick = setInterval(() => {
      const secs = secondsUntilNextWindow();
      setSecondsLeft(secs);
      if (secs === WINDOW_SECONDS) {
        setQrValue(buildQRValue(uid));
      }
    }, 1000);

    return () => clearInterval(tick);
  }, [uid]);

  const circumference = 2 * Math.PI * 20;
  const dashOffset = circumference * (1 - secondsLeft / WINDOW_SECONDS);

  if (!uid) {
    return (
      <div className={styles.Overlay} onClick={onClose}>
        <div className={styles.Modal} onClick={(e) => e.stopPropagation()}>
          <p className={styles.Error}>Not signed in.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.Overlay} onClick={onClose}>
      <div className={styles.Modal} onClick={(e) => e.stopPropagation()}>
        <button
          className={styles.CloseBtn}
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <p className={styles.Title}>Show this at the counter</p>
        <p className={styles.Subtitle}>
          Staff will scan your code to identify you
        </p>

        <div className={styles.QRWrap}>
          <QRCode
            value={qrValue}
            size={200}
            level="L"
            bgColor="#ffffff"
            fgColor="#1a1a1a"
          />
        </div>

        <div className={styles.CountdownWrap}>
          <svg className={styles.CountdownRing} viewBox="0 0 48 48">
            <circle
              cx="24"
              cy="24"
              r="20"
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="4"
            />
            <circle
              cx="24"
              cy="24"
              r="20"
              fill="none"
              stroke="#000000"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              transform="rotate(-90 24 24)"
              style={{ transition: "stroke-dashoffset 0.9s linear" }}
            />
          </svg>
          <span className={styles.CountdownLabel}>{secondsLeft}s</span>
        </div>

        <p className={styles.RefreshNote}>Refreshes every 30 seconds</p>
      </div>
    </div>
  );
};

export default QRAuthCode;
